import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { BusFront, Star } from 'lucide-react';

import { BuslineSchedulesData } from '@area-restrita/api-client/model/buslineSchedulesData';
import { useFavoriteBuslineSchedule } from '@area-restrita/api-client/endpoints/busline-schedules/busline-schedules.gen';
import { abbreviateDay } from '@area-restrita/shared/busline/busline-schedule';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export const buslineScheduleStatusColumn: ColumnDef<BuslineSchedulesData> = {
  id: 'active',
  header: 'Ver rota',
  accessorKey: 'active',
  meta: { className: 'text-center' },
  cell: function BuslineScheduleStatusCell({ row }) {
    const navigate = useNavigate();

    return (
      <Button
        size="icon"
        variant="link"
        onClick={() =>
          navigate({
            to: '/busline-schedules/$buslineScheduleId',
            params: { buslineScheduleId: row.original.id },
          })
        }
      >
        <BusFront
          className="h-6 w-6 self-center"
          color={
            row.original.active ? 'var(--primary)' : 'var(--muted-foreground)'
          }
        />
      </Button>
    );
  },
};

export const buslineSchedulesDaysOfWeekColumn: ColumnDef<BuslineSchedulesData> =
  {
    id: 'daysOfWeek',
    header: 'Dias da semana',
    accessorKey: 'daysOfWeek',
    meta: { className: 'w-[400px] max-w-full' },
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-2">
        {row.original.daysOfWeek.map((dayOfWeek) => (
          <Badge key={`${row.original.id}-day-of-week-${dayOfWeek}`}>
            {abbreviateDay(dayOfWeek)}
          </Badge>
        ))}
      </div>
    ),
  };

export const columns: ColumnDef<BuslineSchedulesData>[] = [
  {
    id: 'search',
    meta: { className: 'hidden' },
  },
  {
    id: 'favorite',
    header: 'Favorito',
    accessorKey: 'favorite',
    meta: { className: 'w-0 max-w-full text-center' },
    cell: function BuslineScheduleFavoriteCell({ row }) {
      const { mutateAsync } = useFavoriteBuslineSchedule();

      return (
        <Button
          size="icon"
          variant="link"
          onClick={() =>
            mutateAsync({
              buslineId: row.original.buslineId,
              data: { favorite: !row.original.favorite },
            })
          }
        >
          <Star
            className="h-6 w-6"
            fill={row.original.favorite ? 'var(--primary)' : 'none'}
            color="var(--primary)"
          />
        </Button>
      );
    },
  },
  buslineScheduleStatusColumn,
  {
    id: 'buslineId',
    header: 'Linha',
    accessorKey: 'buslineId',
    accessorFn: ({ code, name }) => `${code} - ${name}`,
  },
  {
    id: 'startAt',
    header: 'Horário Inicial',
    accessorKey: 'startAt',
  },
  {
    id: 'endAt',
    header: 'Horário Final',
    accessorKey: 'endAt',
  },
  buslineSchedulesDaysOfWeekColumn,
];

export const tenantColumns: ColumnDef<BuslineSchedulesData>[] = [
  ...columns.slice(0, 3),
  {
    id: 'companyIds',
    header: 'Empresa',
    accessorFn: ({ company }) => company.tradingName,
  },
  ...columns.slice(3),
];
